import { authFetch } from '../../api/csrf';
import React from "react";
import {
  Search,
  Info,
  Calendar,
  Clock,
  Loader2,
  Check,
  Eye,
  Trash2,
  Upload,
  AlertCircle,
  Send,
  User,
  Mail,
  X
} from "lucide-react";
import Button from "../common/Button/Button";

const LeaveRequestForm = ({
  isESS = false,
  formData,
  setFormData,
  formErrors = {},
  touched = {},
  handleInputChange,
  handleBlur,
  handleSubmit,
  onCancel,
  leavePolicies = [],
  loadingPolicies = false,
  selectedEmployeeData = null,
  setSelectedEmployeeData,
  employeeSearchOpen = false,
  setEmployeeSearchOpen,
  employeeSearchTerm = "",
  setEmployeeSearchTerm,
  dropdownEmployees = [],
  isLoadingEmployeeSearch = false,
  debouncedEmployeeSearch = "",
  validateField,
  isUploading = false,
  handleFileUpload,
  handleRemoveFile,
  isValidating = false,
  editingRequest = null,
  handleTextKeyPress,
  setSelectedEmployee
}) => {

  return (
    <form onSubmit={handleSubmit} className="p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Select Employee - Hidden for ESS */}
        {!isESS && (
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Employee <span className="text-red-500">*</span>
            </label>
            <div className="relative employee-dropdown-container">
              {/* Display selected employee or placeholder */}
              <div
                onClick={() => {
                  setEmployeeSearchOpen(!employeeSearchOpen);
                  if (!employeeSearchOpen) {
                    setEmployeeSearchTerm("");
                  }
                }}
                tabIndex={0}
                className={`mt-1 w-full border rounded-md px-3 py-2 bg-white cursor-pointer flex items-center justify-between hover:border-gray-400 focus-within:ring-2 focus-within:ring-brand focus-within:outline-none focus-within:border-transparent ${formErrors.employeeId ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
              >
                <span className={`${formData.employeeId ? "text-gray-900" : "text-gray-500"} block w-full break-words whitespace-normal pr-4`}>
                  {formData.employeeId && selectedEmployeeData
                    ? `${selectedEmployeeData.personalInfo?.firstName || ""} ${selectedEmployeeData.personalInfo?.lastName || ""
                    } (${selectedEmployeeData.employeeId || ""})`
                    : formData.employeeId
                      ? `Employee ID: ${formData.employeeId}`
                      : "Select Employee"}
                </span>
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform ${employeeSearchOpen ? "transform rotate-180" : ""
                    }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>

              {/* Dropdown with search */}
              {employeeSearchOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-hidden">
                  {/* Search input */}
                  <div className="p-2 border-b border-gray-200">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search by employee name..."
                        value={employeeSearchTerm}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                          setEmployeeSearchTerm(value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        maxLength={100}
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand text-sm"
                        autoFocus
                      />
                    </div>
                    <div className="mt-1 text-xs text-gray-500 text-right">
                      {(employeeSearchTerm || '').length}/100 characters
                    </div>
                  </div>

                  {/* Results list */}
                  <div className="max-h-64 overflow-y-auto">
                    {isLoadingEmployeeSearch ? (
                      <div className="p-4 text-center text-gray-500 text-sm italic flex items-center justify-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand border-t-transparent"></div>
                        <span>Loading employees...</span>
                      </div>
                    ) : dropdownEmployees && dropdownEmployees.length > 0 ? (
                      dropdownEmployees.map((employee) => (
                        <div
                          key={employee._id}
                          onClick={async () => {
                            setFormData((prev) => ({
                              ...prev,
                              employeeId: employee._id,
                              employeeName: `${employee.personalInfo?.firstName || ""} ${employee.personalInfo?.lastName || ""}`.trim(),
                              employeeEmail: employee.officialEmail || employee.user?.email || "",
                            }));
                            if (setSelectedEmployeeData) setSelectedEmployeeData(employee);
                            if (setSelectedEmployee) setSelectedEmployee(employee);
                            setEmployeeSearchOpen(false);

                            // Fetch full employee data with leaveInfo if not already present
                            if (!employee.leaveInfo) {
                              try {                                const fullEmployeeResponse = await authFetch(
                                  `${process.env.REACT_APP_API_BASE_URL}/hr/employees/${employee._id}`, {
        credentials: 'include',
                                    method: 'GET',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      }
                                  }
                                );

                                if (fullEmployeeResponse.ok) {
                                  const fullEmployeeResult = await fullEmployeeResponse.json();
                                  if (fullEmployeeResult.responseCode === 200 && fullEmployeeResult.data) {
                                    if (setSelectedEmployeeData) setSelectedEmployeeData(fullEmployeeResult.data);
                                  }
                                }
                              } catch (error) {
                              }
                            }
                            setEmployeeSearchTerm("");

                            if (validateField) {
                              await validateField('employeeId', employee._id);
                            }
                          }}
                          className={`px-4 py-2 hover:bg-brand/5 cursor-pointer border-b border-gray-100 ${formData.employeeId === employee._id
                            ? "bg-brand/5"
                            : ""
                            }`}
                        >
                          <div className="text-sm font-medium text-gray-900 break-words whitespace-normal">
                            {employee.personalInfo?.firstName || ""}{" "}
                            {employee.personalInfo?.lastName || ""}
                          </div>
                          <div className="text-xs text-gray-500 break-words whitespace-normal">
                            {employee.employeeId || ""} -{" "}
                            {employee.employmentInfo?.department || ""}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        {debouncedEmployeeSearch && debouncedEmployeeSearch.trim()
                          ? "No employees found"
                          : "Type to search employees..."}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            {formErrors.employeeId && (
              <p className="mt-1 text-sm text-red-600">{formErrors.employeeId}</p>
            )}
            {selectedEmployeeData && (
              <div className="mt-2 p-3 bg-brand/5 rounded-lg">
                <div className="text-sm break-words whitespace-normal">
                  <div className="font-medium text-blue-900 break-words whitespace-normal">
                    {selectedEmployeeData.personalInfo?.firstName || ""}{" "}
                    {selectedEmployeeData.personalInfo?.lastName || ""}
                  </div>
                  <div className="text-blue-700 break-words whitespace-normal">
                    {selectedEmployeeData.officialEmail || selectedEmployeeData.user?.email || "N/A"}
                  </div>
                  <div className="text-brand break-words whitespace-normal">
                    {selectedEmployeeData.employmentInfo?.department || "N/A"} •{" "}
                    {selectedEmployeeData.employmentInfo?.position || "N/A"}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Leave Policy Selection */}
        <div className={isESS ? "md:col-span-2" : ""}>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Leave Policy <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <select
              name="leavePolicyId"
              value={formData.leavePolicyId}
              onChange={handleInputChange}
              onBlur={handleBlur}
              required
              disabled={loadingPolicies}
              className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand pr-10 capitalize ${formErrors.leavePolicyId ? 'border-red-300 bg-red-50' : 'border-gray-300'
                } ${loadingPolicies ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            >
              <option value="">
                {loadingPolicies ? "Loading policies..." : "Select leave policy"}
              </option>
              {leavePolicies.map((policy) => {
                const policyId = policy._id || policy.id;
                return (
                  <option
                    key={policyId}
                    value={policyId}
                    className="truncate capitalize"
                  >
                    {policy.name.length > 35
                      ? `${policy.name.substring(0, 32)}...`
                      : policy.name} ({policy.type}) - {policy.entitlement} days
                  </option>
                );
              })}
            </select>
          </div>
          {formErrors.leavePolicyId && (
            <p className="mt-1 text-sm text-red-600">{formErrors.leavePolicyId}</p>
          )}

          {/* Balance Details Display */}
          {(isESS || selectedEmployeeData) && formData.leavePolicyId && (() => {
            const selectedPolicy = leavePolicies.find(p =>
              p._id === formData.leavePolicyId || p.id === formData.leavePolicyId
            );

            if (!selectedPolicy) return null;

            const leaveInfo = selectedEmployeeData?.leaveInfo || {};
            const leaveTypes = leaveInfo.leaveTypes || [];
            
            const matchingLeaveType = leaveTypes.find(lt => 
              (lt.leaveTypeId?._id || lt.leaveTypeId || lt.id) === (selectedPolicy.leaveTypeId?._id || selectedPolicy.leaveTypeId) ||
              lt.type === selectedPolicy.type
            );

            const isFixed = selectedPolicy.accrualFrequency === 'none';
            const annualQuota = selectedPolicy.entitlement || 0;
            const used = matchingLeaveType?.used || 0;
            const pending = matchingLeaveType?.pending || 0;
            const accrued = isFixed ? annualQuota : (matchingLeaveType?.accruedDays ?? matchingLeaveType?.accrued ?? 0);
            
            const available = matchingLeaveType?.remaining ?? (isFixed 
              ? (annualQuota - used - (isESS ? pending : 0)) 
              : (matchingLeaveType?.remainingDays ?? (accrued - used)));

            return (
              <div className="mt-2 p-4 bg-brand/5 rounded-lg border border-brand/20 w-full shadow-sm">
                <div className="text-sm">
                  <p className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <Info className="h-4 w-4 text-brand" />
                    Current Balance Details:
                  </p>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 w-full">
                    <div className="text-center bg-white rounded-lg py-2 px-1 border border-gray-100 shadow-sm">
                      <div className="text-[10px] uppercase font-bold text-gray-400">Annual Quota</div>
                      <div className="text-sm font-bold text-gray-700">{annualQuota}</div>
                    </div>
                    <div className="text-center bg-white rounded-lg py-2 px-1 border border-gray-100 shadow-sm">
                      <div className="text-[10px] uppercase font-bold text-gray-400">Consumed</div>
                      <div className="text-sm font-bold text-red-600">{used}</div>
                    </div>
                    <div className="text-center bg-brand/10 rounded-lg py-2 px-1 border border-brand/20 shadow-sm">
                      <div className="text-[10px] uppercase font-bold text-brand">Available</div>
                      <div className="text-sm font-black text-brand">{available}</div>
                    </div>
                  </div>
                  
                  <div className="mt-3 flex items-center text-[10px] text-gray-500 italic">
                    <span>Policy: {selectedPolicy.name}</span>
                    <span className="mx-2">•</span>
                    <span>Type: {isFixed ? 'Fixed Quota' : 'Monthly Accrual'}</span>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Start Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Start Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            name="startDate"
            value={formData.startDate}
            onChange={handleInputChange}
            onClick={(e) => {
              try {
                e.target.showPicker?.();
              } catch (error) {}
            }}
            onBlur={handleBlur}
            required
            min={new Date().toISOString().split("T")[0]}
            className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand cursor-pointer ${formErrors.startDate ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
          />
          {formErrors.startDate && (
            <p className="mt-1 text-sm text-red-600">{formErrors.startDate}</p>
          )}
        </div>

        {/* End Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            End Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            name="endDate"
            value={formData.endDate}
            onChange={handleInputChange}
            onClick={(e) => {
              try {
                e.target.showPicker?.();
              } catch (error) {}
            }}
            onBlur={handleBlur}
            required
            disabled={formData.isHalfDay}
            min={formData.startDate || new Date().toISOString().split("T")[0]}
            className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand cursor-pointer ${formErrors.endDate ? 'border-red-300 bg-red-50' : 'border-gray-300'
              } ${formData.isHalfDay ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          />
          {formErrors.endDate && (
            <p className="mt-1 text-sm text-red-600">{formErrors.endDate}</p>
          )}
        </div>

        {/* Requested Days Display */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Requested Days
          </label>
          <input
            type="number"
            value={formData.requestedDays || 0}
            readOnly
            disabled
            className={`w-full px-3 py-2 border rounded-lg bg-gray-100 cursor-not-allowed ${formErrors.duration ? 'border-red-300' : 'border-gray-300'}`}
          />
          {formErrors.duration && (
            <p className="mt-1 text-sm text-red-600">{formErrors.duration}</p>
          )}
          {formErrors.balance && (
            <p className="mt-1 text-sm text-red-600">{formErrors.balance}</p>
          )}
        </div>
      </div>

      {/* Half Day Section */}
      <div className="mt-4 p-4 border border-brand/20 bg-brand/5 rounded-lg">
        <div className="flex items-center mb-4">
          <input
            type="checkbox"
            id="isHalfDay"
            name="isHalfDay"
            checked={formData.isHalfDay}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                isHalfDay: e.target.checked,
                endDate: e.target.checked ? prev.startDate : prev.endDate
              }));
            }}
            className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded cursor-pointer"
          />
          <label htmlFor="isHalfDay" className="ml-2 block text-sm font-medium text-gray-900 cursor-pointer">
            Apply for Half Day
          </label>
        </div>

        {formData.isHalfDay && (
          <div className="flex gap-6 animate-in fade-in duration-200">
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="halfDayType"
                value="first_half"
                checked={formData.halfDayType === 'first_half'}
                onChange={(e) => setFormData(prev => ({ ...prev, halfDayType: e.target.value }))}
                className="h-4 w-4 text-brand focus:ring-brand border-gray-300 cursor-pointer"
              />
              <span className="ml-2 text-sm text-gray-700">First Half</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="halfDayType"
                value="second_half"
                checked={formData.halfDayType === 'second_half'}
                onChange={(e) => setFormData(prev => ({ ...prev, halfDayType: e.target.value }))}
                className="h-4 w-4 text-brand focus:ring-brand border-gray-300 cursor-pointer"
              />
              <span className="ml-2 text-sm text-gray-700">Second Half</span>
            </label>
          </div>
        )}
      </div>

      {/* Reason Field */}
      <div className="mt-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Reason <span className="text-red-500">*</span>
        </label>
        <textarea
          name="reason"
          value={formData.reason}
          onChange={handleInputChange}
          onKeyPress={handleTextKeyPress}
          onBlur={handleBlur}
          required
          rows={2}
          placeholder="Brief reason for leave"
          maxLength={200}
          className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand resize-none ${formErrors.reason ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
        />
        <div className="flex justify-between items-center mt-1">
          {formErrors.reason ? (
            <p className="text-sm text-red-600">{formErrors.reason}</p>
          ) : (
            <div></div>
          )}
          <span className="text-xs text-gray-500">
            {(formData.reason || '').length}/200
          </span>
        </div>
      </div>

      {/* Supporting Document Upload */}
      <div className="mt-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Attach Supporting Document {isESS && "(Optional)"}
        </label>
        <div className={`border-2 border-dashed rounded-xl p-4 transition-all ${
          isUploading ? 'bg-gray-50 border-gray-200' : 
          formData.documentUrl ? 'bg-green-50 border-green-200' : 'border-gray-300 hover:border-brand/50'
        }`}>
          {isUploading ? (
            <div className="flex flex-col items-center justify-center py-2">
              <Loader2 className="h-8 w-8 text-brand animate-spin mb-2" />
              <p className="text-sm text-gray-500 font-medium">Uploading document...</p>
            </div>
          ) : formData.documentUrl ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-green-100 p-2 rounded-lg">
                  <Check className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-700 text-left">Document Attached</p>
                  <a 
                    href={formData.documentUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-brand hover:underline font-medium"
                  >
                    View uploaded file
                  </a>
                </div>
              </div>
              <button
                type="button"
                onClick={handleRemoveFile}
                className="p-2 hover:bg-red-50 rounded-full transition-colors group"
                title="Remove document"
              >
                <Trash2 className="h-4 w-4 text-gray-400 group-hover:text-red-500" />
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center cursor-pointer group">
              <div className="bg-gray-100 p-3 rounded-full group-hover:bg-brand/10 transition-colors mb-3">
                <Upload className="h-6 w-6 text-gray-400 group-hover:text-brand" />
              </div>
              <div className="text-center">
                <p className="text-sm font-bold text-gray-700">Click to upload document</p>
                <p className="text-xs text-gray-500 mt-1">PDF, JPG, PNG (Max 5MB)</p>
              </div>
              <input
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileUpload}
              />
            </label>
          )}
        </div>
      </div>

      {/* Form Actions */}
      <div className="mt-6 flex justify-end space-x-3">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          aria-label="Cancel leave request"
        >
          Cancel request
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={isValidating || isUploading}
          className="flex items-center space-x-2"
          aria-label={editingRequest ? "Update leave request" : "Submit leave request"}
        >
          {isValidating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Validating...</span>
            </>
          ) : (
            <>
              <Send className="h-4 w-4" />
              <span>
                {editingRequest ? "Update leave request" : "Submit leave request"}
              </span>
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

export default LeaveRequestForm;
